import styled from "@emotion/styled";
import msk from "msk";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { PhoneAtom } from "../../atoms/PhoneAtom";
import Configs from "../../config";
import { Country } from "../../types/CountryTypes";
import countries from "../../utils/countries.json";
import Button from "./Button";
import CountryFinderInput from "./CountryFinderInput";

const Wrapper = styled.div`
  max-width: 400px;
  width: 100%;

  display: flex;
  flex-direction: column;
  z-index: 9;

  > div + div {
    margin-top: 16px;
  }
`;

interface PhoneInputProps {
  wrong?: boolean;
}

const InputContainer = styled.div<PhoneInputProps>`
  width: 100%;

  > div {
    background: #f5ebe1;
    border: none;
    border-radius: 48px;
    border: 1px solid ${(props) => (props.wrong ? "red" : "#f5ebe1")};

    > div:first-of-type {
      background: #f5ebe1;
      border: none;
      border-radius: 48px;

      > input {
        background: #f5ebe1;
        /* border: none; */
        border-radius: 48px;
      }
    }
  }
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CountryCodeInput = styled.input`
  width: 20%;
  padding: 15px 0px 15px 15px;
  font-size: 18px;
  appearance: none;

  border-radius: 5px 0px 0px 5px;
  border-right: none;
  color: #2f2f2f;

  :focus {
    outline: none;
  }
`;

const PhoneTextInput = styled.input<PhoneInputProps>`
  width: calc(80% - 7.5px);
  padding: 15px 15px 15px 0;
  font-size: 18px;
  border-radius: 0px 5px 5px 0px;
  color: #2f2f2f;
  border-left: none;
  appearance: none;

  :focus {
    outline: none;
  }

  ::placeholder {
    opacity: 50%;
  }
`;

const NextButtonContainer = styled.div`
  width: 100%;
`;

function getMask(countryCode: string) {
  let result = "(999) 999-9999";
  switch (countryCode) {
    case "+1":
      result = "(999) 999-9999";
      break;
    case "+55":
      result = "99 9 9999-9999";
      break;
    case "+49":
      result = "99 999 999 999";
      break;
    default:
      result = "999999999999";
      break;
  }
  return result;
}

const ONLY_NUMBERS_REGEX = /\d+/g;

interface Props {
  setIsNumberInput: () => void;
  nextEnabled?: {
    exists: boolean;
    type: any;
  };
  handleNextEnabled?: (vle: any) => void;
}

export default function PhoneInput(props: Props): JSX.Element {
  const [countryCode, setCountryCode] = useState<string>("+1");
  const [phone, setPhone] = useState<string>("");
  const [, setPhoneState] = useRecoilState(PhoneAtom);
  const router = useRouter();
  const [disableInput, setDisableInput] = useState(false);

  /**
   * Handles the country code changing both from inside the CountryFinderInput
   * and the input with the "+99..."
   *
   * If the change comes from inside the CountryFinderInput it ignores
   * the "hit country" check and just overwrites it
   *
   * @param code
   * @param force
   */
  function handleCountryCodeChange(code: string, options?: { force: boolean }) {
    // If the string is getting bigger, see if it hit a country
    // as countries have codes that are not inclusive (eg: if it
    // exists a +1 there can't be a +12), hitting a country means
    // we stop accepting more numbers into the input
    if (!options?.force && code.length > countryCode.length) {
      const country = countries.find((elem) => elem.code === `${countryCode}`);

      if (`${countryCode}` === country?.code) return;
    }
    const value = msk.fit(code, "9999");
    setCountryCode(`+${value}`);
  }

  function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = msk.fit(event.target.value, getMask(countryCode));
    setPhone(value);
  }

  useEffect(() => {
    if (!phone) return;
    if (phone.length < 6) return;

    const phoneOnlyNumbers = phone.match(ONLY_NUMBERS_REGEX)?.join("");
    const parsedCountryCode = countryCode.split("+")[1];

    const fullPhone = `${parsedCountryCode}${phoneOnlyNumbers}`;

    const controller = new AbortController();
    const { signal } = controller;
    (async () => {
      props.handleNextEnabled({ exists: false, type: null });

      /**if (fullPhone === "18133244546") {
        props.handleNextEnabled(true);
        return;
      }*/

      const response = await fetch(
        `${Configs.SERVER_URL}/auth/check_phone/${fullPhone}`,
        {
          signal,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const responseJSON = await response.json();

      props.handleNextEnabled(responseJSON.data);
    })();

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
    };
  }, [phone, countryCode]);

  function next() {
    if (countryCode === "+") {
      toast("Put some country code", { type: "error" });
      setDisableInput(true);
      return;
    }

    if (phone === "") {
      toast("Put some phone", { type: "error" });
      setDisableInput(true);
      return;
    }

    if (!props.nextEnabled) {
      toast("There is no account with this phone", { type: "error" });
      setDisableInput(true);
      return;
    }

    let sendPhone = countryCode.replace("+", "");

    sendPhone += phone
      ?.replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .split(" ")
      .join("");
    setPhoneState(sendPhone);
    props.setIsNumberInput();
  }

  function handlePhoneOnkeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      next();
    }
  }

  return (
    <>
      <Wrapper>
        <InputContainer>
          <CountryFinderInput
            countryCode={countryCode}
            setCountryCode={() => {}}
            handleChangeCountry={(country, options) => {
              handleCountryCodeChange((country as Country).code, options);
            }}
          />
        </InputContainer>

        <InputContainer wrong={disableInput}>
          <PhoneInputContainer>
            <CountryCodeInput
              onFocus={() => setDisableInput(false)}
              onChange={(event) => {
                handleCountryCodeChange(event.target.value);
              }}
              value={countryCode}
              onKeyDown={handlePhoneOnkeyDown}
              inputMode="tel"
              pattern="[0-9]*"
              style={{
                background: "#F5EBE1",
                border: "none",
                borderTopLeftRadius: "48px",
                borderBottomLeftRadius: "48px",
              }}
            />
            <PhoneTextInput
              onFocus={() => setDisableInput(false)}
              wrong={disableInput}
              onChange={handlePhoneChange}
              value={phone}
              onKeyDown={handlePhoneOnkeyDown}
              placeholder={getMask(countryCode)}
              pattern="[0-9]*"
              inputMode="tel"
              style={{
                background: "#F5EBE1",
                border: "none",
                borderTopRightRadius: "48px",
                borderBottomRightRadius: "48px",
              }}
            />
          </PhoneInputContainer>
        </InputContainer>

        <NextButtonContainer>
          <Button
            disabled={!props.nextEnabled.exists}
            text="Continue"
            width="100%"
            height="46px"
            fontSize="18px"
            onClick={next}
          />
        </NextButtonContainer>
      </Wrapper>
    </>
  );
}
